import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ErrorState } from '@/components/ui/error-state';
import { PageLayout } from '@/components/ui/page-layout';
import {
  Field,
  SectionCard,
  SectionDivider,
  StatRow,
  StatTile,
} from '@/components/ui/section-card';
import { SkeletonForm } from '@/components/ui/skeleton';
import { paymentMethodLabel } from '@/constants/service-payment-methods';
import { CHARGE_AMOUNTS } from '@/content/payments/charge';
import {
  formatPayoutAccount,
  PAYOUT_STATUS_LABELS,
  RECEIVABLES_PAYOUT_DETAIL,
} from '@/content/payments/receivables';
import { usePaymentPayout } from '@/hooks/use-payment-payouts';
import { PROVIDER_PAYOUT_STATUS } from '@/types/payment-payout';
import { getApiErrorMessage } from '@/utils/api-error';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';

function goBack() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/payments' as never);
}

function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

/** Arredonda o acúmulo em ponto flutuante (0.1 + 0.2 !== 0.3) para 2 casas — mesmo critério de `PayoutDetailDto`. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Detalhe do repasse (T6.2, PLANO 6.4/6.9 "extrato por pedido: bruto, taxa,
 * líquido"). `PayoutChargeItemDto` já traz `clientName`/`serviceName` de
 * cada cobrança (T7.1) — antes disso, a tela fazia um `GET /charge/:id` por
 * cobrança só para o nome (T6.5 trocou pelo campo direto, sem N chamadas).
 * O total das cobranças pode não bater com o valor do repasse por
 * arredondamento ou ajuste do Stripe; a diferença aparece como "Ajustes",
 * nunca escondida.
 */
export default function PayoutDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const payout = usePaymentPayout(id);

  if (payout.isLoading) {
    return (
      <PageLayout title={RECEIVABLES_PAYOUT_DETAIL.title} onBack={goBack}>
        <SkeletonForm fields={4} showSubmit={false} />
      </PageLayout>
    );
  }

  if (errorStatus(payout.error) === 403) {
    return (
      <PageLayout title={RECEIVABLES_PAYOUT_DETAIL.title} onBack={goBack}>
        <ErrorState
          icon="lock-closed-outline"
          title="Sem acesso"
          message={getApiErrorMessage(payout.error, 'Você não pode ver este repasse.')}
        />
      </PageLayout>
    );
  }

  if (payout.error || !payout.data) {
    return (
      <PageLayout title={RECEIVABLES_PAYOUT_DETAIL.title} onBack={goBack}>
        <ErrorState
          title="Não foi possível carregar o repasse"
          message={getApiErrorMessage(payout.error, 'Tente de novo em instantes.')}
          onRetry={() => payout.refetch()}
        />
      </PageLayout>
    );
  }

  const data = payout.data;
  const adjustment = round2(data.amount - data.chargesNetTotal);
  const hasAdjustment = Math.abs(adjustment) >= 0.01;

  return (
    <PageLayout title={RECEIVABLES_PAYOUT_DETAIL.title} onBack={goBack}>
      <SectionCard
        icon="arrow-down-circle-outline"
        title={RECEIVABLES_PAYOUT_DETAIL.title}
        badge={PAYOUT_STATUS_LABELS[data.status]}
        badgeTone={
          data.status === PROVIDER_PAYOUT_STATUS.PAID
            ? 'success'
            : data.status === PROVIDER_PAYOUT_STATUS.FAILED
              ? 'danger'
              : data.status === PROVIDER_PAYOUT_STATUS.CANCELED
                ? 'muted'
                : 'neutral'
        }
      >
        <StatRow>
          <StatTile label="Valor" value={formatCurrency(data.amount)} emphasis />
          <StatTile label="Data" value={formatDate(data.arrivalDate ?? data.createdAt)} />
        </StatRow>

        <Field
          label={RECEIVABLES_PAYOUT_DETAIL.accountLabel}
          value={formatPayoutAccount(null, data.bankLast4)}
        />

        {data.status === PROVIDER_PAYOUT_STATUS.FAILED && data.failureMessage ? (
          <Alert variant="error">
            <AlertDescription>{data.failureMessage}</AlertDescription>
          </Alert>
        ) : null}
      </SectionCard>

      <SectionCard icon="receipt-outline" title={RECEIVABLES_PAYOUT_DETAIL.chargesTitle}>
        {data.charges.length === 0 ? (
          <ThemedText className="text-sm text-neutral-500">
            {RECEIVABLES_PAYOUT_DETAIL.chargesEmpty}
          </ThemedText>
        ) : (
          <View>
            {data.charges.map((charge, index) => {
              const clientName = charge.clientName || RECEIVABLES_PAYOUT_DETAIL.clientFallback;
              const methodLabel = charge.paymentMethodUsed
                ? paymentMethodLabel(charge.paymentMethodUsed)
                : null;

              return (
                <View
                  key={charge.id}
                  className={
                    index < data.charges.length - 1
                      ? 'gap-1 border-b border-neutral-100 py-3'
                      : 'gap-1 py-3'
                  }
                >
                  <View className="flex-row items-start justify-between gap-3">
                    <View className="min-w-0 flex-1 gap-0.5">
                      <ThemedText
                        className="font-semibold text-sm text-neutral-900"
                        numberOfLines={1}
                      >
                        {clientName}
                      </ThemedText>
                      <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
                        {[
                          charge.serviceName,
                          methodLabel,
                          charge.paidAt ? formatDate(charge.paidAt) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </ThemedText>
                    </View>
                    <ThemedText className="shrink-0 font-bold text-sm text-neutral-900">
                      {charge.netAmount != null ? formatCurrency(charge.netAmount) : '—'}
                    </ThemedText>
                  </View>
                  <ThemedText className="text-xs text-neutral-400">
                    {CHARGE_AMOUNTS.grossLabel}{' '}
                    {charge.grossAmount != null ? formatCurrency(charge.grossAmount) : '—'} ·{' '}
                    {CHARGE_AMOUNTS.feeLabel}{' '}
                    {charge.feeAmount != null ? formatCurrency(charge.feeAmount) : '—'}
                  </ThemedText>
                </View>
              );
            })}
          </View>
        )}

        <SectionDivider />

        <StatRow>
          <StatTile
            label={RECEIVABLES_PAYOUT_DETAIL.chargesTotalLabel}
            value={formatCurrency(data.chargesNetTotal)}
          />
          <StatTile
            label={RECEIVABLES_PAYOUT_DETAIL.payoutAmountLabel}
            value={formatCurrency(data.amount)}
            emphasis
          />
        </StatRow>

        {hasAdjustment ? (
          <Field
            label={RECEIVABLES_PAYOUT_DETAIL.adjustmentsLabel}
            value={formatCurrency(adjustment)}
            hint={RECEIVABLES_PAYOUT_DETAIL.adjustmentsHint}
          />
        ) : null}
      </SectionCard>
    </PageLayout>
  );
}
